import EmployeeDayPlanItem from '../../../Domain/EmployeeDayPlanItem';
import InputTypesEnum from '../../../Enums/InputTypesEnum';
import AvailabilityPlannerService from '../../../Services/AvailabilityPlannerService';

// BambooHR-owned days lock the absence: 'approved' (confirmed) and 'requested' (pending). The work part
// stays editable; only the absence type/hours are disabled, and the day can't be cleared/deleted.
const isBambooLocked = (item) => item.Status === 'approved' || item.Status === 'requested';
const lockedNote = (item) =>
  item.Status === 'approved'
    ? `Approved absence — locked: ${item.AbsenceType} (${item.AbsenceHours}h)`
    : item.Status === 'requested'
    ? `Requested in BambooHR — pending approval: ${item.AbsenceType} (${item.AbsenceHours}h)`
    : '';

export default class AvailabilityPlannerPanelDescription {
  static titleForCreate = 'Availability';
  static titleForEdit = 'Availability';
  static finishButtonLabel = 'Save';
  static itemType = EmployeeDayPlanItem;
  static serviceFunction = AvailabilityPlannerService.SaveDay;
  // Saving is silent (CD-5512): planning a month means many saves in a row, so a confirmation popup on each
  // one is pure friction — the calendar refreshing behind the closing panel is the confirmation. This matches
  // the quick actions (bolt + right-click menu), which have always saved without a popup. The "planning only,
  // request leave in BambooHR" caveat this popup used to carry lives on in the page's user guide.
  static suppressSuccessMessage = true;

  // Delete = clear the day(s) (save with zeroed hours over the selected range, which the backend removes).
  // Hidden for BambooHR-owned days (approved/requested — their absence is locked). The wizard shows Delete
  // only when editing existing entries — for a single day or a drag-selected range that already holds entries.
  static deleteFunction = (item) =>
    AvailabilityPlannerService.SaveDay({ ...item, Location: '', WorkingHours: 0, AbsenceType: '', AbsenceHours: 0 });
  static deleteConfirmMessage =
    'This will remove the availability entry for the selected day(s). Do you want to continue?';
  static messageOnDelete = 'The selected day(s) have been cleared.';
  static canDelete = (item) => !isBambooLocked(item);

  static pagesInGeneralEditWizzard = [
    {
      label: 'Availability',
      fields: [
        {
          fieldName: 'StartDate',
          label: 'Start Date',
          className: 'col-6',
          type: InputTypesEnum.Date,
          validityChecker: (value) => (value ? '' : 'Start date is required.'),
          onFieldChange: (editedItem) => {
            if (editedItem.EndDate < editedItem.StartDate) {
              editedItem.EndDate = editedItem.StartDate;
            }
          },
        },
        {
          fieldName: 'EndDate',
          label: 'End Date',
          className: 'col-6',
          type: InputTypesEnum.Date,
          validityChecker: (value) => (value ? '' : 'End date is required.'),
        },
        {
          fieldName: 'Location',
          label: 'Work location',
          className: 'col-6',
          type: InputTypesEnum.Select,
          selectvalues: [
            { value: '', label: '— none —' },
            { value: 'office', label: 'Office' },
            { value: 'home', label: 'Home office' },
            { value: 'businesstrip', label: 'Business trip' },
          ],
          validityChecker: (value, item) =>
            item.WorkingHours > 0 && !value ? 'Work location is required when working hours are set.' : '',
        },
        {
          fieldName: 'WorkingHours',
          // Leaving both working and absence hours at 0 clears the day (reverts it to the default).
          label: 'Working hours (0 working + 0 absence clears the day)',
          className: 'col-6',
          type: InputTypesEnum.Number,
          validityChecker: (value, item) => {
            const working = Number(value) || 0;
            if (working < 0) return 'Working hours cannot be negative.';
            if (working + (Number(item.AbsenceHours) || 0) > 12) {
              return 'Working and absence hours together cannot exceed 12.';
            }
            return '';
          },
        },
        {
          fieldName: 'AbsenceType',
          label: 'Absence type',
          className: 'col-6',
          type: InputTypesEnum.Select,
          disabled: (item) => isBambooLocked(item),
          // Selecting an absence zeroes the work part, resets the location to the user's default, and
          // prefills a FULL absence day (only when absence hours are still 0, so typed hours are never
          // overwritten); the user can then adjust the hours or re-add work (e.g. a part-day).
          onFieldChange: (editedItem) => {
            if (editedItem.AbsenceType) {
              editedItem.WorkingHours = 0;
              editedItem.Location = editedItem.DefaultLocation || 'office';
              if (!Number(editedItem.AbsenceHours)) {
                editedItem.AbsenceHours = editedItem.StandardDailyHours || 8;
              }
            }
          },
          // A disabled field renders blank, so surface the locked absence value as an info note.
          info: (item) => lockedNote(item),
          selectvalues: [
            { value: '', label: '— none —' },
            { value: 'vacation', label: 'Vacation' },
            { value: 'sick', label: 'Sick leave' },
            { value: 'unpaid', label: 'Unpaid' },
            { value: 'bankholiday', label: 'Bank holiday' },
            { value: 'other', label: 'Other' },
            { value: 'toil', label: 'Time off in lieu' },
          ],
          validityChecker: (value, item) =>
            item.AbsenceHours > 0 && !value ? 'Absence type is required when absence hours are set.' : '',
        },
        {
          fieldName: 'AbsenceHours',
          label: 'Absence hours',
          className: 'col-6',
          type: InputTypesEnum.Number,
          disabled: (item) => isBambooLocked(item),
          validityChecker: (value, item) => {
            const absence = Number(value) || 0;
            const working = Number(item.WorkingHours) || 0;
            if (absence < 0) return 'Absence hours cannot be negative.';
            // A clear (both hours 0) is allowed even if a type is still selected, so only require absence
            // hours when the day isn't being cleared.
            if (working + absence > 0 && item.AbsenceType && absence <= 0) {
              return 'Absence hours are required when an absence type is selected.';
            }
            if (absence + working > 12) {
              return 'Working and absence hours together cannot exceed 12.';
            }
            return '';
          },
        },
      ],
    },
  ];
}
